import React, { useState, useEffect } from 'react';
import { shopVar } from '@/lib/shopStyles';
import clsx from 'clsx';
import { useCart } from '@/providers/CartProvider';
import * as m from "@/paraglide/messages.js"

export const ShopLockOverlay: React.FC = () => {
    const { applyCoupon } = useCart();
    const [couponCode, setCouponCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const addCouponCode = async () => {
        if (!couponCode.trim()) return;
        
        setLoading(true);
        setError('');
        const result = await applyCoupon(couponCode);
        if (result === true) {
            setCouponCode('');
        } else {
            setError(result);
        }
        setLoading(false);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            addCouponCode();
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div 
                className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
                style={{
                    backgroundColor: shopVar('--shop-panelBackgroundColor'),
                    color: shopVar('--shop-textColor'),
                }}
            >
                {/* Lock Icon */}
                <div className="flex justify-center mb-4">
                    <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: shopVar('--shop-borderColor') }}
                    >
                        <svg 
                            className="w-8 h-8" 
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                            style={{ color: shopVar('--shop-textColor') }}
                        >
                            <path 
                                fillRule="evenodd" 
                                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" 
                                clipRule="evenodd" 
                            />
                        </svg>
                    </div>
                </div>

                {/* Title */}
                <h2 className="text-xl font-semibold text-center mb-2">
                    {m.protected_shop()}
                </h2>

                {/* Description */}
                <p className="text-center mb-6 opacity-80">
                    {m.protected_shop_description()}
                </p>

                {/* Coupon Code Input */}
                <div className="space-y-4">
                    <div className="flex items-center gap-x-2">
                        <input
                            type="text"
                            placeholder="Enter access code"
                            className={clsx(
                                'flex-1 rounded-md border-0 py-2 px-3 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6',
                                {
                                    'opacity-60 pointer-events-none': loading,
                                    'ring-red-500': !!error,
                                    'ring-gray-300': !error,
                                },
                                'placeholder:text-[color:var(--shop-textColor)] placeholder:opacity-60'
                            )}
                            style={{
                                color: shopVar('--shop-textColor'),
                                backgroundColor: shopVar('--shop-panelBackgroundColor'),
                                // @ts-expect-error --tw-ring-color is defined by tailwindcss
                                '--tw-ring-color': shopVar('--shop-borderColor'),
                            }}
                            value={couponCode}
                            onChange={e => setCouponCode(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={loading}
                        />
                        <button
                            className={clsx(
                                'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                                {
                                    'opacity-60 cursor-not-allowed': loading,
                                    'hover:opacity-90': !loading,
                                }
                            )}
                            style={{
                                backgroundColor: shopVar('--shop-ctaButtonColor'),
                                color: shopVar('--shop-ctaButtonTextColor'),
                            }}
                            onClick={addCouponCode}
                            disabled={loading}
                        >
                            {loading ? '...' : m.unlock()}
                        </button>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div 
                            className="text-sm rounded-md p-2"
                            style={{
                                backgroundColor: shopVar('--shop-productErrorMessageBackground'),
                                color: shopVar('--shop-productErrorMessageText'),
                            }}
                        >
                            {error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
